import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import MarkerClusterer from '@googlemaps/markerclustererplus'
import Div from 'components/Div'
import { withResponsiveContext } from 'decorators/Responsive'
import { coreResource } from 'env'

const MapContainer = styled(Div)`
  height: 400px;
  width: 100%;
`

class GoogleMap extends React.PureComponent {
  static propTypes = {
    Container: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    withClustering: PropTypes.bool,
    withMarker: PropTypes.bool,
    zoom: PropTypes.number,
    initialCenter: PropTypes.object.isRequired,
    onDragEnd: PropTypes.func,
    onDragStart: PropTypes.func,
    onZoomChanged: PropTypes.func,
    mapOptions: PropTypes.object,
    mapMarkers: PropTypes.array,
    mapApi: PropTypes.object.isRequired, // injected by withMapContext
    registerMap: PropTypes.func.isRequired, // injected by withMapContext
    unregisterMap: PropTypes.func.isRequired, // injected by withMapContext
    mapServices: PropTypes.object, // injected by withMapContext
    responsive: PropTypes.object // injected by withResponsiveContext
  }

  static defaultProps = {
    withMarker: true,
    mapOptions: {},
    Container: MapContainer,
    zoom: 17,
    onDragStart: () => {},
    onDragEnd: () => {},
    onZoomChanged: () => {}
  }

  constructor (props) {
    super(props)
    this.mapRef = React.createRef()
    this.state = {
      mapRendered: false
    }
  }

  componentDidMount () {
    this.renderMap()
  }

  componentDidUpdate (prevProps) {
    const { responsive } = this.props
    const { mapRendered } = this.state
    if (!mapRendered) {
      this.renderMap()
    }
    if (responsive.desktop !== prevProps.responsive.desktop) {
      this.setMapControls()
    }
  }

  componentWillUnmount () {
    this.props.unregisterMap()
  }

  renderMap = () => {
    const { mapServices, zoom, initialCenter, registerMap, responsive } = this.props
    const isDesktop = responsive.desktop
    const { latitude, longitude } = initialCenter
    if (mapServices) {
      this.map = new mapServices.Map(this.mapRef.current, {
        zoom,
        center: { lat: latitude, lng: longitude },
        disableDefaultUI: true,
        gestureHandling: isDesktop ? 'cooperative' : 'greedy'
      })
      this.setState({ mapRendered: true }, () => registerMap(
        this.map,
        {
          getZoomLevel: this.getZoomLevel,
          setMapCenter: this.setMapCenter,
          getMapCenterAddress: this.getMapCenterAddress
        },
        () => {
          this.setMapControls()
          this.bindEventListeners()
          this.setMapMarkers()
        }
      ))
    }
  }

  // https://developers.google.com/maps/documentation/javascript/examples/control-replacement
  isFullScreenMode = () => {
    if (!this.map) return
    const element = this.map.getDiv().firstChild
    return (document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement) === element
  }

  // WORKAROUND: Detect full screen mode to show google maps marker
  onBoundsChanged = () => {
    const { withMarker } = this.props
    if (this.isFullScreenMode() && withMarker) {
      if (!this.marker) {
        const { mapServices } = this.props
        this.marker = new mapServices.Marker({ position: this.map.getCenter(), map: this.map })
      }
      this.marker.setMap(this.map)
      this.centerMarkerListener = this.map.addListener('center_changed', () => this.marker.setPosition(this.map.getCenter()))
    } else {
      if (this.marker) {
        this.marker.setMap(null)
        this.centerMarkerListener && this.centerMarkerListener.remove()
      }
    }
  }

  setMapControls = () => {
    const { responsive, mapOptions } = this.props
    const isDesktop = responsive.desktop
    this.map && this.map.setOptions({
      zoomControl: isDesktop,
      fullscreenControl: isDesktop,
      ...mapOptions
    })
  }

  setMapMarkers = () => {
    const { mapMarkers, mapServices, withClustering } = this.props

    if (this.map && (mapMarkers || []).length > 0) {
      const bounds = new mapServices.LatLngBounds()

      mapMarkers.forEach((marker) => {
        if (!(marker instanceof mapServices.Marker)) {
          throw new Error('Not a valid marker')
        }

        marker.setMap(this.map)
        bounds.extend(marker.getPosition())
      })

      if (withClustering) {
        // eslint-disable-next-line no-new
        new MarkerClusterer(this.map, mapMarkers, {
          imagePath: coreResource('/images/m')
        })
      }

      // center the map to the geometric center of all markers
      this.map.setCenter(bounds.getCenter())
      this.map.fitBounds(bounds)

      // if zoom is bigger than 10, set it back to city level zoom (10)
      setTimeout(() => {
        const currentZoom = this.map.getZoom()
        const cityLevelZoom = 10

        this.map.setZoom(
          Math.min(currentZoom, cityLevelZoom)
        )
      }, 1000)
    }
  }

  // Exposed Map helper
  getZoomLevel = () => {
    return this.map.getZoom()
  }

  // Exposed Map helper
  getMapCenterAddress = () => {
    const { mapApi } = this.props
    const center = this.map.getCenter()
    return center && mapApi.reverseGeoCode({ latitude: center.lat(), longitude: center.lng() })
  }

  // Exposed Map helper
  setMapCenter = ({ latitude, longitude }) => {
    this.map.setCenter({ lat: latitude, lng: longitude })
    this.map.setZoom(this.props.zoom)
  }

  bindEventListeners = () => {
    const { onDragEnd, onDragStart, onZoomChanged } = this.props
    this.map.addListener('dragend', onDragEnd)
    this.map.addListener('dragstart', onDragStart)
    this.map.addListener('zoom_changed', onZoomChanged)
    this.map.addListener('bounds_changed', this.onBoundsChanged)
  }

  render () {
    const { className, Container } = this.props
    return <Container className={className} ref={this.mapRef} />
  }
}

export default withResponsiveContext(GoogleMap)
